import { deserialize } from 'serializr'
import { makeAutoObservable, runInAction } from 'mobx'
import { EventGroup } from './eventGroup'
import { ValidateAddToCartController } from './validateAddToCartController'
import { ValidateCartController } from './validateCartController'
import { ModalController } from './modalController'
import { Customer } from './customer'
import Settings from '../components/settings'
import { Addon } from './addon'
import GA4Tracking from '../utils/ga4-tracking'

export class Cart {
  /**
   * @type {Map<string, EventGroup>}
   */
  eventGroupsStore = new Map()
  /**
   * @type {EventGroup[]}
   */
  get eventGroups() {
    return Array.from(this.eventGroupsStore.values())
  }

  static instance = null

  customer = new Customer()

  coupon = null
  couponTitle = null

  /**
   *
   * @return {Cart}
   */
  static getInstance() {
  }

  constructor() {
    makeAutoObservable(this)
  }

  async addEventGroups(modules, show_modal = true) {
    let invalidEvents = []

    const validation_data =
      await ValidateAddToCartController.getInstance().validate(
        modules.map(m => m.id)
      )
    invalidEvents = Object.values(validation_data).filter(o => !o.valid)

    if (invalidEvents.length === 0) {
      runInAction(() => {
        modules.forEach(module => {
          this.eventGroupsStore.set(module.id, new EventGroup(module))
        })

        // Google Analytics (GA4)
        GA4Tracking.pushEvent(
          'add_to_cart',
          GA4Tracking.getItemsFromModules(modules),
          {
            'item_list_id': 'single',
            'item_list_name': 'Single Product Page',
          }
        )

        if(typeof gtag === "function") {
          gtag('config', 'UA-36099469-3', {
            'page_title' : 'Tilføj til kurven',
            'page_path': '/add-to-cart/'
          });
        }

        if (show_modal) {
          let instance = ModalController.getInstance()
          instance.show(modules)
        }
        this.updateContent(validation_data)
      })
    }
  }
  /**
   * @param {import('./participant').Participant} participant
   */
  addParticipantToAllModules(participant) {
    this.eventGroups.forEach(module => {
      module.addParticipant(participant)
    })
  }

  /**
   * @param {string} moduleId
   */
  removeModule(moduleId) {
    let eventGroup = this.eventGroupsStore.get(moduleId)

    // Google Analytics (GA4)
    GA4Tracking.pushEvent(
      'remove_from_cart',
      GA4Tracking.getItemsAndAddonsFromCartEventGroups([eventGroup])
    )

    this.eventGroupsStore.delete(moduleId)

    if(typeof gtag === "function") {
      gtag('config', 'UA-36099469-3', {
        'page_title' : 'remove-from-Cart',
        'page_path': '/remove-from-cart'
      });
    }

  }
  /**
   * @param {string} id
   * @return {boolean}
   */
  hasModule(id) {
    return this.eventGroupsStore.has(id)
  }

  get cartTotal() {
    return this.eventGroups
      .map(module => module.totalPriceIncludingAddons ?? 0)
      .reduce((a, b) => a + b, 0)
  }
  get vat() {
    return this.eventGroups
      .map(module => module.totalVatIncludingAddons ?? 0)
      .reduce((a, b) => a + b, 0)
  }
  get cartTotalWithVat() {
    return this.cartTotal + this.vat
  }
  get cartSubTotal() {
    return this.eventGroups
      .map(module => module.priceIncludingAddons ?? 0)
      .reduce((a, b) => a + b, 0)
  }
  get cartTotalDiscount() {
    return this.eventGroups
      .map(module => module.totalDiscountIncludingAddons ?? 0)
      .reduce((a, b) => a + b, 0)
  }
  get cartMailDiscount() {
    return this.eventGroups
      .map(module => module.mailDiscountIncludingAddons ?? 0)
      .reduce((a, b) => a + b, 0)
  }
  get cartCouponDiscount() {
    return this.eventGroups
      .map(module => module.couponDiscountIncludingAddons ?? 0)
      .reduce((a, b) => a + b, 0)
  }
  get cartLoyaltyDiscount() {
    return this.eventGroups
      .map(module => module.loyaltyDiscountIncludingAddons ?? 0)
      .reduce((a, b) => a + b, 0)
  }
  empty() {
    this.eventGroupsStore.clear()
  }

  async validateContent() {
    if (this.eventGroups.length < 1) {
      return
    }

    let validateCartController = ValidateCartController.getInstance()
    const validation_data = await validateCartController.validate(
      Object.fromEntries(this.eventGroupsStore.entries()),
      this.customer,
      this.coupon
    )
    if (!validateCartController.isAllValid) {
      return
    }
    this.updateContent(validation_data)
  }

  updateContent(validation_data) {
    Object.entries(validation_data).forEach(([key, value]) => {
      let eventGroup = this.eventGroupsStore.get(key)

      eventGroup.title = value.data?.title ?? eventGroup.title
      eventGroup.subtitle = value.data?.subtitle ?? eventGroup.subtitle
      eventGroup.pricePerParticipant = value.data?.total_price ?? eventGroup.pricePerParticipant
      eventGroup.vatPerParticipant = value.data?.total_price_vat ?? eventGroup.vatPerParticipant
      eventGroup.spots_left = value.data?.spots_left ?? eventGroup.spots_left
      eventGroup.featured_image = value.data?.featured_image ?? eventGroup.featured_image

      eventGroup.addons = deserialize(Addon,value.data?.addons) ?? []

      eventGroup.chosen_addons?.forEach(addon => {
        addon.updateContent(value.chosen_addons.find((el) => el.data.id === addon.id))
      })

      eventGroup.requiredFields = value.data?.required_fields ?? {}

      eventGroup.participants.forEach(participant => {
        participant.updateContent(value)
      })
    })
  }
}
